import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ref, createRef, Ref } from "lit/directives/ref.js";

import "@shoelace-style/shoelace/dist/components/card/card.js";
import "@shoelace-style/shoelace/dist/components/input/input.js";
import "@shoelace-style/shoelace/dist/components/button-group/button-group.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/tooltip/tooltip.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";

import { SlInput } from "@shoelace-style/shoelace";

@customElement("c-editor-link-edit-box")
class LinkEditBox extends LitElement {
    // anchorNode is reactively set to the node at the start
    // of the active link by the popup manager.
    @property({ attribute: false })
    anchorNode;

    @property()
    hrefValue: string;

    private setHref: (newHref: string) => void;

    private close: () => void;
    private removeLink: () => void;
    private hrefInput: Ref<SlInput> = createRef();

    constructor({
        href,
        close,
        removeLink,
        autofocus,
    }: {
        href: { value: string; setter: (value: string) => void };
        close: () => void;
        removeLink: () => void;
        autofocus: boolean;
    }) {
        super();
        this.hrefValue = href.value;
        this.setHref = href.setter;

        this.close = close;
        this.removeLink = removeLink;
        this.autofocus = autofocus;
    }

    static styles = css`
        .link-form {
            display: flex;
            align-items: center;
        }
        .href-input {
            width: 450px;
            padding: 0 var(--sl-spacing-medium);
        }
    `;

    async firstUpdated() {
        if (this.autofocus) {
            await customElements.whenDefined("sl-input");
            await this.updateComplete;
            this.hrefInput.value?.focus();
        }
    }

    updated() {
        const nodeHref = this?.anchorNode?.marks?.find?.(
            (mark) => mark.type?.name === "link"
        )?.attrs?.href;
        if (nodeHref && nodeHref !== this.hrefValue) {
            this.hrefValue = nodeHref;
        }
    }

    render() {
        return html`<sl-card
            @keyup=${(e: KeyboardEvent) => {
                e.stopImmediatePropagation();
                if (e.key === "Escape") {
                    this.close();
                }
            }}
            @keydown=${(e: KeyboardEvent) => {
                e.stopImmediatePropagation();
                if (e.key === "Enter") {
                    e.preventDefault();
                    this.setHref(this.hrefInput.value?.value);
                }
            }}
        >
            <form
                class="link-form"
                @submit=${(e: SubmitEvent) => {
                    e.preventDefault();
                    this.setHref(this.hrefInput.value?.value);
                }}
            >
                <sl-input
                    placeholder="https://www.example.com/"
                    value=${this.hrefValue}
                    ${ref(this.hrefInput)}
                    class="href-input"
                ></sl-input>

                <sl-button-group>
                    <sl-tooltip content="Update link url">
                        <sl-button type="submit">
                            <sl-icon
                                slot="prefix"
                                name="check-lg"
                                library="component-assets"
                            />
                        </sl-button>
                    </sl-tooltip>
                    <sl-tooltip content="Remove link">
                        <sl-button
                            @click=${() => {
                                this.removeLink();
                            }}
                        >
                            <sl-icon
                                slot="prefix"
                                name="trash"
                                library="component-assets"
                            />
                        </sl-button>
                    </sl-tooltip>
                </sl-button-group>
            </form>
        </sl-card>`;
    }
}

export { LinkEditBox };
